import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { errorToast, successToast } from "@telluscompany/design-system";
import { useLocalization } from "@i18n";

import {
  axiosDeleteData,
  axiosPatchData,
  axiosPostData,
  axiosPutData,
} from "@shared/api/clients";

import {
  ClientDetail,
  CreateClientRequest,
  UpdateClientRequest,
} from "./clients.types";

const CLIENTS_ENDPOINT = "v1/managerial/tenants";

export const createClient = async (
  payload: CreateClientRequest,
): Promise< string > => {
  return axiosPostData<string, CreateClientRequest>(
    CLIENTS_ENDPOINT,
    payload,
  )
};

export const editClient = async ({
  id,
  ...payload
}: UpdateClientRequest & { id: string }): Promise<ClientDetail> => {
  return axiosPutData<ClientDetail, UpdateClientRequest>(
    `${CLIENTS_ENDPOINT}/${id}`,
    payload,
  );
};

export const deleteClient = async (id: string): Promise<void> => {
  await axiosDeleteData<void>(`${CLIENTS_ENDPOINT}/${id}`);
};

export const uploadClientLogo = async ({
  id,
  logo,
}: {
  id: string;
  logo: File;
}): Promise<void> => {
  const formData = new FormData();
  formData.append("logo", logo);
  await axiosPatchData<void, FormData>(
    `${CLIENTS_ENDPOINT}/${id}/logo`,
    formData,
  );
};

export const useCreateClientMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: createClient,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["clients"],
        exact: false,
      });
      successToast({
        description: translate("common.createSuccess"),
      });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorCreating"),
      });
    },
  });
};

export const useEditClientMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: editClient,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["clients"],
        exact: false,
      });
      successToast({
        description: translate("common.editSuccess"),
      });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorEditing"),
      });
    },
  });
};

export const useDeleteClientMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: deleteClient,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["clients"],
        exact: false,
      });
      successToast({
        description: translate("common.deleteSuccess"),
      });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorDeleting"),
      });
    },
  });
};

export const useUploadLogoMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadClientLogo,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["clients"],
        exact: false,
      });
    },
  });
};
